import PropTypes from 'prop-types';
import React, { useRef, useEffect, useCallback } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

import { capturePostHogException } from 'src/utils/posthog';

import ErrorView from 'src/sections/error/error-view';

const ERROR_THRESHOLD = 2;
const CHUNK_RELOAD_KEY = 'voolist:chunk-reloaded';

const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' ||
  /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || '');

export default function ErrorBoundary ({ children }) {
  const errorCountRef = useRef(0);

  const handleError = useCallback((error, info) => {
    errorCountRef.current += 1;
    // Report React render errors to PostHog Error Tracking (no-op when disabled).
    capturePostHogException(error, {
      source: 'react_error_boundary',
      componentStack: info?.componentStack,
    });
    // Stale-client recovery: after a deploy, an open tab references chunk filenames
    // that no longer exist on the server. A hard reload pulls the new index.html
    // (and its updated chunk manifest). sessionStorage guards against reload loops
    // if the chunk genuinely can't be fetched (e.g., real outage).
    if (isChunkLoadError(error) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      window.location.reload();
    }
  }, []);

  const handleReset = useCallback(() => {
    // Count persists across resets so repeated crashes show contact CTAs
  }, []);

  useEffect(() => {
    // Clear the guard after a delay long enough that any near-mount chunk failure
    // has already tripped the boundary — so a genuine outage shows the error screen
    // instead of looping, but a later navigation in the same session can still auto-recover.
    const timer = setTimeout(() => {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ReactErrorBoundary
      onError={handleError}
      onReset={handleReset}
      fallbackRender={({ error, resetErrorBoundary }) => (
        <ErrorView
          error="This page didn't load properly. Try again."
          showContactOptions={errorCountRef.current >= ERROR_THRESHOLD}
          onRetry={isChunkLoadError(error)
            ? () => window.location.reload()
            : resetErrorBoundary}
        />
      )}
    >
      {children}
    </ReactErrorBoundary>
  );
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};